import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { BACKEND_URL } from "@/constants/api";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { getPageComponent } from "@/reports/registry";
import { getFolderLogos } from "@/utils/imageCache";

export const ReportCard = () => {
  const { id } = useParams();
  const { getToken, userId } = useAuth();
  const location = useLocation();

  const [reportType, setReportType] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [contextInfo, setContextInfo] = useState(null);
  const [logos, setLogos] = useState([]);
  const [logosVersion, setLogosVersion] = useState(null);
  const [ownSignature, setOwnSignature] = useState(null); // { data, version } | null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const fetchReport = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/reports/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch report");
        const json = await res.json();
        setReportType(json.reportType);
        setGroupId(json.groupId);

        // The caller's own signature (for the one-click Sign button + stamping it
        // into the preview). Best-effort; the editor works without it.
        fetch(`${BACKEND_URL}api/user/signature`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((sig) => {
            if (sig && !controller.signal.aborted) {
              setOwnSignature({ data: sig.signatureBase64 ?? null, version: sig.signatureVersion ?? null });
            }
          })
          .catch(() => { /* signature is optional */ });

        // Fetch Report Folder for grade + schoolYear + logosVersion context.
        // On the shared route the owner-only /report-folders/:id endpoint 404s,
        // so use the shared endpoint (it returns the same fields and authorizes
        // shared viewers) — otherwise logos never render for shared members.
        try {
          const folderUrl = location.pathname.startsWith("/shared-report-folders/")
            ? `${BACKEND_URL}api/shared-report-folders/${json.groupId}`
            : `${BACKEND_URL}api/report-folders/${json.groupId}`;
          const groupRes = await fetch(folderUrl, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          if (groupRes.ok) {
            const groupJson = await groupRes.json();
            setContextInfo({
              grade: groupJson.grade || '',
              schoolYear: groupJson.schoolYear || '',
              studentName: json.studentName || '',
              oen: json.data?.oen?.trim() || '',
            });

            // Class logos: cheap logosVersion rides this payload; fetch the heavy
            // bytes (via cache) then publish logos + version together, so the
            // preview's version-keyed regen fires once the logos are actually loaded.
            if (groupJson.logosVersion) {
              getFolderLogos({ userId, folderId: json.groupId, version: groupJson.logosVersion, getToken })
                .then((fetched) => {
                  if (controller.signal.aborted) return;
                  setLogos(fetched);
                  setLogosVersion(groupJson.logosVersion);
                })
                .catch(() => { /* logos are non-critical; render without them */ });
            }
          }
        } catch (err) {
          if (err.name === "AbortError") throw err;
          // context is best-effort; don't fail the whole page
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
    return () => controller.abort();
  }, [id, getToken, userId, location.pathname]);

  if (loading) return (
    <div className="container my-6 space-y-4">
      <div className="h-7 w-48 bg-muted animate-pulse rounded" />
      <div className="h-4 w-64 bg-muted animate-pulse rounded" />
      <div className="space-y-3 pt-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    </div>
  );
  if (error) return <div className="container my-6 text-destructive">Error: {error}</div>;

  const FormComponent = getPageComponent(reportType);
  if (!FormComponent) {
    return (
      <div className="container my-6 text-muted-foreground">
        Unknown report type: <code>{reportType}</code>
      </div>
    );
  }

  // Determine back URL: shared Report Folder or owned Report Folder
  const isShared = location.pathname.startsWith("/shared-report-folders/");
  const backUrl = isShared
    ? location.pathname.replace(/\/reports\/[^/]+$/, "")
    : groupId
    ? `/report-folders/${groupId}`
    : "/report-folders";

  const readOnly = isShared && location.state?.permission === "view";

  return (
    <ErrorBoundary>
      <FormComponent reportId={id} backUrl={backUrl} contextInfo={contextInfo} readOnly={readOnly} logos={logos} logosVersion={logosVersion} isOwner={!isShared} ownSignature={ownSignature} />
    </ErrorBoundary>
  );
};
